import { useMutation, useQueryClient } from 'react-query'

import { CustomEvent } from '../../../../enums/customEvent'
import { QueryKeys } from '../../../../enums/queryKeys'
import { APIError } from '../../../../utils'
import { dispatchCustomEvent } from '../../../../utils/customEvent'
import { updateOrganization, UpdateOrganizationPayload, UpdateOrganizationResponseData } from '../query-api'
import { Organization } from '../types/organization'
import { useUserOrganization } from '../userOrganizationContext'

interface UseUpdateUserProps {
  onError?: (error?: APIError) => void
  onSettled?: (data: UpdateOrganizationResponseData | undefined, error: APIError | null | undefined) => void
  onSuccess?: (data: UpdateOrganizationResponseData) => void
}

export interface UpdateOrganizationProps {
  organizationId: string
  payload: UpdateOrganizationPayload
}

export const useUpdateOrganization = (props?: UseUpdateUserProps) => {
  const queryClient = useQueryClient()

  const { onError, onSuccess, onSettled } = props || {}
  const { organization } = useUserOrganization()
  const {
    mutate: update,
    mutateAsync: updateAsync,
    isLoading: isProcessing,
    ...rest
  } = useMutation(
    ({ organizationId, payload }: UpdateOrganizationProps) => updateOrganization(organizationId, payload),
    {
      onSuccess: (data) => {
        onSuccess?.(data)

        const updateOrganizationResponseData = data?.organizations.find(
          (updatedOrganization) => updatedOrganization?.id === organization?.id,
        )

        if (updateOrganizationResponseData) {
          dispatchCustomEvent<Partial<Organization>>(CustomEvent.OrganizationUpdated, updateOrganizationResponseData)
          queryClient.invalidateQueries([QueryKeys.UserOrganization, organization?.id])
          queryClient.invalidateQueries([QueryKeys.UserOrganizations])
        }
      },
      onError,
      onSettled,
    },
  )

  return { update, updateAsync, isProcessing, ...rest }
}
